import React, { useState, useRef, useEffect, useCallback, memo, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Loader2,
  Camera,
  Clock,
  Trash2,
  ArrowUp,
  ChevronUp,
  X,
  Mic,
  Square
} from 'lucide-react';
import { chatAPI, projectAPI, createQueryKey, objectiveMatchAPI } from '../services/api';
import StreamingMessage from './StreamingMessage';
import InteractiveCards from './InteractiveCards';
import CardErrorBoundary from './CardErrorBoundary';
import LoadingIndicator from './LoadingIndicator';
import useUnifiedWebSocket from '../hooks/useUnifiedWebSocket';
import useAutoScroll from '../hooks/useAutoScroll';
import usePaginatedMessages from '../hooks/usePaginatedMessages';
import useWelcomeMessage from '../hooks/useWelcomeMessage';
import { useCompass } from '../hooks/useCompass';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'react-hot-toast';
import { useProjectStore } from '../stores';
import uploadService from '../services/uploadService';
import CameraModal from './CameraModal';
import FullscreenImageSpotlightViewer from './FullscreenImageSpotlightViewer';
import { ParticipantNotificationManager } from './ParticipantNotification';
import InviteTeamCard from './InviteTeamCard';
import ProjectObjectivesCard from './ProjectObjectivesCard';
import { useAuth } from '../contexts/AuthContext';

// Feature flags / guards
// Client auto-welcome disabled; server handles welcome on project start

const OPTIMISTIC_TIMESTAMP_BUFFER_MS = 250;

const getMessageTimestamp = (message) => {
  try {
    const raw = (message && (message.createdAt || message.created_at || message.timestamp || message.created || message.created_at_ts)) || null;
    if (!raw) return 0;
    if (typeof raw === 'number') return raw;
    return new Date(raw).getTime() || 0;
  } catch (_) {
    return 0;
  }
};

const createCardEntry = (cards, version = 1, status = 'final') => ({
  cards: Array.isArray(cards) ? [...cards] : [],
  version: typeof version === 'number' ? version : 1,
  status: status || 'final'
});

const extractCardsFromEntry = (entry) => {
  if (!entry) return [];
  if (Array.isArray(entry)) return entry;
  if (Array.isArray(entry.cards)) return entry.cards;
  return [];
};

const extractVersionFromEntry = (entry) => {
  if (!entry) return 0;
  if (typeof entry === 'object' && typeof entry.version === 'number') return entry.version;
  return Array.isArray(entry) ? 1 : 0;
};

const extractStatusFromEntry = (entry) => {
  if (!entry) return 'final';
  if (typeof entry === 'object' && typeof entry.status === 'string') return entry.status;
  return 'final';
};

const IsolatedChatInput = memo(({ onSendMessage, isConnected, isGenerating, onStopGeneration, projectId, onSubmittedWithImage = null, externalCommand = null, onExternalCommandHandled = null, isReadOnly = false }) => {
  const [localMessage, setLocalMessage] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [externalAutoSendCapture, setExternalAutoSendCapture] = useState(false);
  const [cameraOverlayHint, setCameraOverlayHint] = useState(null);
  const [pendingExternalMetadata, setPendingExternalMetadata] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const shouldUploadRecordingRef = useRef(true);
  const speechRecognitionRef = useRef(null);
  const speechRecognitionStopRequestedRef = useRef(false);
  const speechWarningShownRef = useRef(false);
  const voiceTranscriptRef = useRef('');
  const committedTranscriptRef = useRef('');
  const isRecordingRef = useRef(false);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);
  // Handle external commands (e.g., open camera with prefilled text)
  useEffect(() => {
    if (!externalCommand) return;
    try {
      if (externalCommand.type === 'open_camera') {
        const prefillText = externalCommand.prefillText || '';
        setLocalMessage(prefillText);
        setExternalAutoSendCapture(!!externalCommand.autoSend);
        setCameraOverlayHint(externalCommand.overlayHint || null);
        setPendingExternalMetadata(externalCommand.metadata || null);
        setShowCameraModal(true);
      } else if (externalCommand.type === 'prefill_text') {
        const prefillText = externalCommand.prefillText || '';
        setLocalMessage(prefillText);
        setPendingExternalMetadata(externalCommand.metadata || null);
      }
    } finally {
      if (typeof onExternalCommandHandled === 'function') {
        try { onExternalCommandHandled(); } catch (_) {}
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalCommand]);

  const stopSpeechRecognition = useCallback((options = {}) => {
    const { clearTranscript = false } = options;
    const recognition = speechRecognitionRef.current;
    speechRecognitionStopRequestedRef.current = true;
    if (recognition) {
      try {
        recognition.onresult = null;
        recognition.onerror = null;
        recognition.onend = null;
        recognition.stop();
      } catch (_) {}
      speechRecognitionRef.current = null;
    }
    if (clearTranscript) {
      committedTranscriptRef.current = '';
      voiceTranscriptRef.current = '';
      setLocalMessage('');
    }
  }, []);

  const startSpeechRecognition = useCallback(() => {
    if (typeof window === 'undefined') return false;
    const SpeechRecognitionConstructor = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognitionConstructor) {
      if (!speechWarningShownRef.current) {
        toast('Live voice transcription is not supported in this browser. Audio will still be sent.');
        speechWarningShownRef.current = true;
      }
      return false;
    }
    try {
      speechRecognitionStopRequestedRef.current = false;
      const recognition = new SpeechRecognitionConstructor();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = (typeof navigator !== 'undefined' && navigator.language) ? navigator.language : 'en-US';
      recognition.onresult = (event) => {
        let committed = committedTranscriptRef.current || '';
        let interimTranscript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          const text = result[0]?.transcript || '';
          if (result.isFinal) {
            committed = `${committed} ${text}`.trim();
          } else {
            interimTranscript = text;
          }
        }
        committedTranscriptRef.current = committed.trim();
        const combined = [committedTranscriptRef.current, interimTranscript].filter(Boolean).join(' ').trim();
        voiceTranscriptRef.current = combined;
        if (isRecordingRef.current) {
          setLocalMessage(combined);
        }
      };
      recognition.onerror = (event) => {
        console.warn('Speech recognition error:', event);
      };
      recognition.onend = () => {
        if (!speechRecognitionStopRequestedRef.current) {
          try {
            recognition.start();
          } catch (restartError) {
            console.warn('Failed to restart speech recognition:', restartError);
          }
        }
      };
      recognition.start();
      speechRecognitionRef.current = recognition;
      committedTranscriptRef.current = '';
      voiceTranscriptRef.current = '';
      return true;
    } catch (error) {
      console.error('Failed to start speech recognition:', error);
      return false;
    }
  }, []);

  const handleStartRecording = useCallback(async () => {
    if (isReadOnly) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      shouldUploadRecordingRef.current = true;
      committedTranscriptRef.current = '';
      voiceTranscriptRef.current = '';
      setLocalMessage('');

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const shouldUpload = shouldUploadRecordingRef.current;
        shouldUploadRecordingRef.current = true;
        speechRecognitionStopRequestedRef.current = true;
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        // Create file with timestamp for uniqueness
        const filename = `voice_${Date.now()}.webm`;
        const audioFile = new File([audioBlob], filename, { type: 'audio/webm' });
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
        
      stopSpeechRecognition({ clearTranscript: !shouldUpload });
        const transcriptForMessage = (voiceTranscriptRef.current || '').trim();

        if (!shouldUpload) {
          audioChunksRef.current = [];
          setIsUploading(false);
          setIsRecording(false);
          setLocalMessage('');
          return;
        }

        // Auto submit
        setIsUploading(true);
        try {
            const uploadResult = await uploadService.uploadAudio(audioFile);
            if (uploadResult.success) {
                const audioUrl = uploadResult.data.url;
                const metadata = { audioUrl };
                if (transcriptForMessage) {
                  metadata.voiceTranscript = transcriptForMessage;
                }
                const messageText = transcriptForMessage || "🎤 Voice Message";
                onSendMessage(messageText, null, metadata); 
                setLocalMessage('');
                voiceTranscriptRef.current = '';
                committedTranscriptRef.current = '';
            } else {
                toast.error(`Audio upload failed: ${uploadResult.error}`);
            }
        } catch (error) {
             console.error("Audio upload error:", error);
             toast.error('Failed to upload audio');
        } finally {
            setIsUploading(false);
            setIsRecording(false); // Ensure state is reset
            audioChunksRef.current = [];
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      isRecordingRef.current = true;
      startSpeechRecognition();
    } catch (error) {
      console.error('Error accessing microphone:', error);
      toast.error('Could not access microphone. Please check permissions.');
    }
  }, [isReadOnly, onSendMessage, startSpeechRecognition, stopSpeechRecognition]);

  const handleStopRecording = useCallback((options = {}) => {
    const { cancel = false } = options;
    const recorder = mediaRecorderRef.current;
    if (!recorder) {
      return;
    }
    shouldUploadRecordingRef.current = !cancel;
    if (recorder.state !== 'inactive') {
      recorder.stop();
    }
    if (cancel) {
      stopSpeechRecognition({ clearTranscript: true });
    }
    setIsRecording(false);
  }, [stopSpeechRecognition]);
  useEffect(() => {
    if (!isRecording || !isGenerating) {
      return;
    }
    handleStopRecording({ cancel: true });
    toast('Voice recording stopped automatically because another action started.');
  }, [isGenerating, isRecording, handleStopRecording]);

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        shouldUploadRecordingRef.current = false;
        try {
          mediaRecorderRef.current.stop();
        } catch (_) {}
      }
      stopSpeechRecognition({ clearTranscript: true });
    };
  }, [stopSpeechRecognition]);
  
  const handleChange = useCallback((e) => {
    setLocalMessage(e.target.value);
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if ((!localMessage.trim() && !selectedImage) || isGenerating || isUploading) return;
    
    let imageUrl = null;
    
    // Upload image if selected
    if (selectedImage) {
      setIsUploading(true);
      try {
        const uploadResult = await uploadService.uploadImage(selectedImage);
        if (uploadResult.success) {
          imageUrl = uploadResult.data.url;
        } else {
          toast.error(`Image upload failed: ${uploadResult.error}`);
          setIsUploading(false);
          return;
        }
      } catch (error) {
        toast.error('Failed to upload image');
        setIsUploading(false);
        return;
      }
      setIsUploading(false);
    }
    
    // Generate contextual text if uploading from an observe card
    let messageText = localMessage.trim();
    if (!messageText && imageUrl && pendingExternalMetadata?.selected_action_card) {
      // Auto-generate contextual text for observe card photo uploads
      const cardTitle = pendingExternalMetadata?.action_card_title;
      if (cardTitle) {
        messageText = `Photo for: ${cardTitle}`;
      } else if (pendingExternalMetadata?.action_card_type === 'observe') {
        messageText = 'Observation photo';
      }
    }
    // Send message with optional image
    onSendMessage(messageText, imageUrl, pendingExternalMetadata);
    
    // Notify parent if image was included
    try {
      if (onSubmittedWithImage && (selectedImage || imageUrl)) {
        onSubmittedWithImage();
      }
    } catch (_) {}
    
    // Clear form
    setLocalMessage('');
    setSelectedImage(null);
    setImagePreview(null);
    setPendingExternalMetadata(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [localMessage, selectedImage, isGenerating, isUploading, onSendMessage, onSubmittedWithImage, pendingExternalMetadata]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  }, [handleSubmit]);

  const handleImageSelect = useCallback(() => {
    // Disable all interactions in read-only mode
    if (isReadOnly) {
      return;
    }
    
    setShowCameraModal(true);
  }, [isReadOnly]);


  const handleCameraPhotoCapture = useCallback(async (file, options = { autoSend: false }) => {
    // Disable all interactions in read-only mode
    if (isReadOnly) {
      return;
    }
    
    // Validate the captured photo
    const validation = uploadService.validateImageFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    const shouldAutoSend = Boolean(options?.autoSend) || Boolean(externalAutoSendCapture);

    if (shouldAutoSend && isConnected && !isGenerating) {
      // Auto-upload and send immediately with current text
      setIsUploading(true);
      try {
        const uploadResult = await uploadService.uploadImage(file);
        if (!uploadResult.success) {
          toast.error(`Image upload failed: ${uploadResult.error}`);
          setIsUploading(false);
          return;
        }
        const imageUrl = uploadResult.data.url;
        // Generate contextual text if uploading from an observe card
        let messageText = localMessage.trim();
        if (!messageText && pendingExternalMetadata?.selected_action_card) {
          // Auto-generate contextual text for observe card photo uploads
          const cardTitle = pendingExternalMetadata?.action_card_title;
          if (cardTitle) {
            messageText = `Photo for: ${cardTitle}`;
          } else if (pendingExternalMetadata?.action_card_type === 'observe') {
            messageText = 'Observation photo';
          }
        }
        onSendMessage(messageText, imageUrl, pendingExternalMetadata);
        
        // Notify parent that a submission with image occurred
        try {
          if (onSubmittedWithImage) {
            onSubmittedWithImage();
          }
        } catch (_) {}
        // Clear input state
        setLocalMessage('');
        setSelectedImage(null);
        if (imagePreview) {
          URL.revokeObjectURL(imagePreview);
        }
        setImagePreview(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        setPendingExternalMetadata(null);
      } catch (err) {
        console.error('Failed to auto-upload image:', err);
        toast.error('Failed to upload image');
      } finally {
        setIsUploading(false);
        setShowCameraModal(false);
        setExternalAutoSendCapture(false);
        setCameraOverlayHint(null);
      }
    } else {
      // Default behavior: attach image and let user add text and submit
      setSelectedImage(file);
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
      setShowCameraModal(false);
      setExternalAutoSendCapture(false);
      setCameraOverlayHint(null);
      if (!isConnected) {
        toast.error('Not connected. Image attached — try again in a moment.');
      } else if (isGenerating) {
        toast('Assistant is responding. Image attached — send when ready.');
      }
    }
  }, [isReadOnly, isConnected, isGenerating, onSendMessage, localMessage, imagePreview, onSubmittedWithImage, externalAutoSendCapture, pendingExternalMetadata]);

  const handleCloseCameraModal = useCallback(() => {
    setShowCameraModal(false);
  }, []);


  const handleFileChange = useCallback((e) => {
    // Disable all interactions in read-only mode
    if (isReadOnly) {
      return;
    }
    
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    const validation = uploadService.validateImageFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      e.target.value = ''; // Clear input
      return;
    }

    setSelectedImage(file);
    
    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setImagePreview(previewUrl);
  }, [isReadOnly]);

  const handleRemoveImage = useCallback(() => {
    // Disable all interactions in read-only mode
    if (isReadOnly) {
      return;
    }
    
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [isReadOnly, imagePreview]);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
    }
  }, [localMessage]);

  return (
    <div className={`p-1 px-1.5 mb-3 md:mb-4 w-[95%] max-w-3xl mx-auto border rounded-3xl shadow focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-xs ${
      isFocused ? 'border-gray-500 dark:border-gray-400' : 'border-gray-300 dark:border-gray-600'
    }`}>
      <form onSubmit={handleSubmit} className="flex flex-col gap-2">
        {/* Image Preview */}
        {imagePreview && (
          <div className="p-2 border-b border-gray-200 dark:border-gray-600">
            <div className="relative inline-block max-w-full">
              <img 
                src={imagePreview} 
                alt="Upload preview" 
                className="max-w-full sm:max-w-40 max-h-32 rounded-lg border border-gray-300 dark:border-gray-600 object-cover w-auto h-auto"
              />
              <button
                type="button"
                onClick={handleRemoveImage}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-red-600 text-xs"
                title="Remove image"
              >
                <X size={12} />
              </button>
            </div>
            {/* <div className="text-xs text-gray-500 mt-1">
              {selectedImage?.name} ({uploadService.formatFileSize(selectedImage?.size || 0)})
            </div> */}
          </div>
        )}

        <div className="flex items-center gap-2">
          {/* Image Upload Button - Left Side */}
          <div className="relative">
            <button
              type="button"
              onClick={handleImageSelect}
              disabled={isReadOnly || !isConnected || isGenerating || isUploading}
              className="p-2 w-10 h-10 rounded-full disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center flex-shrink-0 bg-green-200 dark:bg-green-400/20 text-green-600 dark:text-green-500 border border-green-600/10 dark:border-green-500/10"
              title="Add image"
              aria-label="Add image"
            >
              {isUploading ? (
                <Loader2 size={20} className="animate-spin" />
              ) : (
                <Camera size={20} />
              )}
            </button>
          </div>

          <div className="flex-1 min-w-0">
            <textarea
              ref={textareaRef}
              value={localMessage}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              placeholder={isConnected ? "Ask anything" : "Connecting..."}
              disabled={isReadOnly || !isConnected || isGenerating || isUploading}
              className="w-full py-3 px-1 border-0 resize-none outline-none focus:ring-0 text-sm bg-transparent placeholder-gray-400 dark:placeholder-gray-500 text-gray-900 dark:text-white"
              rows={1}
              style={{ minHeight: '20px', maxHeight: '120px' }}
            />
            
            
          </div>
          
          <div className="flex items-center gap-2">
            {/* Mic Button */}
            {(!isGenerating || isRecording) && (
                <button
                  type="button"
                  onClick={isRecording ? handleStopRecording : handleStartRecording}
                  disabled={isReadOnly || !isConnected || (isUploading && !isRecording)}
                  className={`p-2 w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                    isRecording 
                      ? 'bg-red-500 text-white hover:bg-red-600 animate-pulse' 
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                  title={isRecording ? "Stop recording" : "Voice input"}
                >
                  {isRecording ? <Square size={16} fill="currentColor" /> : <Mic size={18} />}
                </button>
            )}

            {isGenerating ? (
              <button
                type="button"
                onClick={onStopGeneration}
                className="p-2 bg-red-500 dark:bg-red-600 w-10 h-10 text-white rounded-full hover:bg-red-600 dark:hover:bg-red-500 flex items-center justify-center"
                title="Stop generation"
                aria-label="Stop generation"
              >
                <div className="w-3 h-3 bg-white rounded-sm"></div>
              </button>
            ) : (
              <button
                type="submit"
                disabled={isReadOnly || (!localMessage.trim() && !selectedImage) || !isConnected || isUploading}
                className="p-2 bg-gray-500 dark:bg-gray-600 w-10 h-10 text-white rounded-full hover:bg-gray-600 dark:hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                aria-label="Send message"
              >
                <ArrowUp size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          accept={uploadService.getSupportedMimeTypes()}
          onChange={handleFileChange}
          className="hidden"
        />
      </form>
      
      {/* Camera Modal */}
      <CameraModal
        isOpen={showCameraModal}
        onClose={handleCloseCameraModal}
        onPhotoCapture={handleCameraPhotoCapture}
        overlayHint={cameraOverlayHint}
      />
    </div>
  );
});

/**
 * Chat History List Component
 */
const ChatHistoryList = memo(({ 
  chats, 
  activeChatId, 
  onChatSelect, 
  onDeleteChat, 
  isLoading 
}) => {

  if (isLoading) {
    return (
      <div className="flex justify-center p-4">
        <Loader2 className="animate-spin" size={24} />
      </div>
    );
  }

  if (!chats?.length) {
    return (
      <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
        No nature conversations yet
      </div>
    );
  }

  return (
    <div className="space-y-1 p-2">
      {chats.map((chat) => (
        <div
          key={chat.id}
          className={`flex items-center justify-between p-2 rounded cursor-pointer group transition-colors ${
            activeChatId === chat.id ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700' : 'hover:bg-gray-50 dark:hover:bg-gray-800'
          }`}
          onClick={() => onChatSelect(chat.id)}
        >
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
              {chat.title || 'Nature Chat'}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
              <Clock size={10} />
              {formatDistanceToNow(new Date(chat.createdAt), { addSuffix: true })}
            </div>
          </div>
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDeleteChat(chat.id);
            }}
            className="opacity-0 group-hover:opacity-100 p-1 text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-opacity"
            aria-label="Delete chat"
          >
            <Trash2 size={12} />
          </button>
        </div>
      ))}
    </div>
  );
});

/**
 * Main Chat Section Component
 */
const ChatSection = ({ project, projectId, chatId, setActiveChatId, isReadOnly = false, onAISuggestionsChange = null, disableAutoScroll = false, onRequestFinishProject = null, onObjectiveConfirmed = null, onOpenGoalsModal = null, onSubmittedWithImage = null, onPersistLocationPin = null, onInteractiveCardInteraction = null, onMessagesChange = null }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  // Stores
  const { activeChatId, setActiveChatId: storeSetActiveChatId } = useProjectStore();
  
  // Use passed props or fallback to params and store
  const { projectId: paramsProjectId, chatId: paramsChatId } = useParams();
  const effectiveProjectId = projectId || paramsProjectId;
  const effectiveChatId = chatId || paramsChatId || activeChatId;
  const effectiveSetActiveChatId = setActiveChatId || storeSetActiveChatId;
  
  // Debug logging for read-only and chat ID
  // if (effectiveProjectId && effectiveChatId) {
  //   const willShowInput = !!(effectiveChatId && !isReadOnly);
  //   const willShowBanner = !!(effectiveChatId && isReadOnly);
  //   console.log('[ChatSection] Render check:', {
  //     effectiveChatId,
  //     isReadOnly,
  //     chatIdProp: chatId,
  //     paramsChatId,
  //     activeChatId,
  //     willShowInput,
  //     willShowBanner,
  //     conditionCheck: {
  //       hasChatId: !!effectiveChatId,
  //       notReadOnly: !isReadOnly,
  //       final: willShowInput,
  //       bannerCondition: `effectiveChatId=${!!effectiveChatId} && isReadOnly=${isReadOnly} = ${willShowBanner}`
  //     }
  //   });
    
  //   // Warn if input should be hidden but might still render
  //   if (isReadOnly && effectiveChatId) {
  //     console.warn('[ChatSection] ⚠️ READ-ONLY MODE: Input should be HIDDEN. isReadOnly=', isReadOnly, 'effectiveChatId=', effectiveChatId, 'Banner should show:', willShowBanner);
  //   }
  // }

  // Project query
  const { data: projectQueryData } = useQuery({
    queryKey: createQueryKey.project(effectiveProjectId),
    queryFn: () => projectAPI.getById(effectiveProjectId),
    enabled: !!effectiveProjectId,
    staleTime: 0, // ✅ STALE DATA FIX: Always fetch fresh data for ongoing projects
    refetchOnMount: true, // ✅ STALE DATA FIX: Always refetch when revisiting
    refetchOnWindowFocus: true, // ✅ STALE DATA FIX: Refetch when user returns to tab
    select: (response) => response.data?.data?.project || response.data?.project || response.data
  });
  
  // Use the project from props first, fallback to query data
  const effectiveProject = project || projectQueryData;

  const handleLocationCardCollapsed = useCallback(() => {
    if (typeof onAISuggestionsChange === 'function') {
      // console.log('🗺️ Hiding location pins for collapsed card');
      onAISuggestionsChange([]);
    }
  }, [onAISuggestionsChange]);
  
  // Local state
  const [activeTab, setActiveTab] = useState('chat');
  const [isGenerating, setIsGenerating] = useState(false);
  const [optimisticMessage, setOptimisticMessage] = useState(null);
  const optimisticMessageRef = useRef(null);
  useEffect(() => {
    optimisticMessageRef.current = optimisticMessage;
  }, [optimisticMessage]);
  const optimisticTimestampMapRef = useRef(new Map());
  const latestChronologicalTimestampRef = useRef(0);
  const [streamingMessage, setStreamingMessage] = useState(null);
  const [isReplacingStreamingMessage, setIsReplacingStreamingMessage] = useState(false);
  // Show a spinner after text streaming completes while waiting for cards to arrive/persist
  const [awaitingCardsMessageId, setAwaitingCardsMessageId] = useState(null);
  const [, setScrollLocked] = useState(false);
  // Removed client-side chat creation state
  // Removed client-side chat creation timestamp ref
  const connectedAtRef = useRef(null);
  // Consolidated state for better performance
  const [chatState, setChatState] = useState({
    messageCards: new Map(),
    croppedImages: new Map(), // LEGACY - for backward compat
    userLocation: null,
    objectiveNotifications: []
  });
  
  // Compass heading from shared hook (Device Orientation API - works even when stationary)
  const { heading: compassHeading } = useCompass();
  const compassHeadingRef = useRef(null);
  useEffect(() => {
    compassHeadingRef.current = compassHeading;
  }, [compassHeading]);
  
  // Extract state values for use in callbacks
  const { messageCards, croppedImages, userLocation, objectiveNotifications } = chatState;
  const messageCardsRef = useRef(messageCards);
  useEffect(() => {
    messageCardsRef.current = messageCards;
  }, [messageCards]);

  // Memoized haversine calculation to avoid recreating on every render
  const haversineKm = useCallback((lat1, lng1, lat2, lng2) => {
    if (lat1 == null || lng1 == null || lat2 == null || lng2 == null) return null;
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) + Math.cos(lat1 * Math.PI/180) * Math.cos(lat2 * Math.PI/180) * Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }, []);

  // Handle location card expansion/collapse for map integration
  const handleLocationCardExpanded = useCallback((locations) => {
    if (typeof onAISuggestionsChange === 'function' && locations && Array.isArray(locations)) {
      const suggestions = locations.map((location, idx) => {
        const lat = Number(location.location.lat);
        const lng = Number(location.location.lng);
        const distKm = (userLocation && typeof userLocation.lat === 'number' && typeof userLocation.lng === 'number')
          ? haversineKm(userLocation.lat, userLocation.lng, lat, lng)
          : null;
        return {
          id: `location-expanded-${location.id || idx}-${lat.toFixed(6)}-${lng.toFixed(6)}`,
          name: location.name || 'Location',
          distance: distKm,
          distanceText: distKm != null ? `${distKm.toFixed(1)}km` : 'Nearby',
          description: location.description || '',
          source: 'ai-temp',
          coordinates: { lat, lng },
          confidence: 0.9,
          fullMatch: undefined
        };
      });

      // console.log(`🗺️ Showing ${suggestions.length} location pins for expanded card:`, suggestions.map(s => `${s.name} (${s.id})`));
      onAISuggestionsChange(suggestions);
    }
  }, [haversineKm, userLocation, onAISuggestionsChange]);
  
  // Timeout/guard refs
  const fallbackTimeoutRef = useRef(null);
  const scrollLockTimeoutRef = useRef(null);
  const welcomeTimeoutRef = useRef(null);
  const streamingIdRef = useRef(null);

  // Auto-scroll functionality (must be before other hooks that depend on refs)
  const { 
    containerRef,
    endRef,
    scrollToBottom,
    autoScrollOnNewContent,
    autoScrollOnInitialLoad,
    autoScrollOnStreaming,
    autoScrollOnStreamingComplete,
    forceScrollToBottom,
    isUserScrolledUp: userHasScrolled,
    isNearBottom,
    resetScrollState,
    handleScroll
  } = useAutoScroll();

  

  // Server state (persisted messages)
  const {
    messages: serverMessages,
    isLoading: isMessagesLoading,
    loadMore,
    hasMore,
    isLoadingMore,
    addMessage
  } = usePaginatedMessages(effectiveChatId || null);

  const lastPersistedServerTimestamp = useMemo(() => {
    const list = Array.isArray(serverMessages) ? serverMessages : [];
    if (!list.length) return 0;
    return getMessageTimestamp(list[list.length - 1]);
  }, [serverMessages]);

  // Unified message list: server state + client state
  const allMessages = useMemo(() => {
    const serverList = Array.isArray(serverMessages) ? [...serverMessages] : [];
    const clientList = [];
    
    // Debug: Track serverMessages changes
    // if (process.env.NODE_ENV !== 'production') {
    //   console.debug('[msgs] serverMessages changed', { 
    //     count: serverList.length,
    //     messages: serverList.map(m => ({ id: m.id, role: m.role, content: m.content?.substring(0, 30) + '...' }))
    //   });
    // }
    
    // Add streaming message (client state)
    if (streamingMessage && streamingMessage.id && !serverList.find(m => m && m.id === streamingMessage.id)) {
      // Use a stable timestamp based on the last server message to prevent reordering during streaming
      const lastServerMessage = serverList[serverList.length - 1];
      const baseTimestamp = lastServerMessage?.createdAt ? new Date(lastServerMessage.createdAt).getTime() : Date.now();
      const stableTimestamp = new Date(baseTimestamp + 100).toISOString(); // 100ms after last message
      
      clientList.push({
        id: streamingMessage.id,
        role: 'assistant',
        content: streamingMessage.content || '',
        createdAt: stableTimestamp,
        chatId: effectiveChatId,
        _isStreaming: true
      });
    }
    
    // Add optimistic message (client state) - already has future timestamp
    // But only if there's no matching server message with the same client_message_id or ID
    if (optimisticMessage) {
      const clientMessageId = optimisticMessage.client_message_id;
      const optimisticId = optimisticMessage.id;
      
      const hasMatchingServerMessage = serverList.some(m => {
        if (!m) return false;
        // Match by client_message_id (most reliable for optimistic messages)
        if (clientMessageId && m.client_message_id === clientMessageId && m.role === optimisticMessage.role) {
          return true;
        }
        // Also match by ID if optimistic message has a real ID (shouldn't happen, but defensive)
        if (optimisticId && m.id === optimisticId && m.role === optimisticMessage.role) {
          return true;
        }
        return false;
      });
      
      // Only add optimistic message if no matching server message exists
      if (!hasMatchingServerMessage) {
        clientList.push({
          ...optimisticMessage,
          _isOptimistic: true
        });
      }
    }
    
    // Combine server and client state
    const combined = [...serverList, ...clientList];
    
    return combined.sort((a, b) => {
      const ta = getMessageTimestamp(a);
      const tb = getMessageTimestamp(b);
      if (ta !== tb) return ta - tb;
      
      // Tiebreaker: streaming messages come before saved messages with same timestamp
      if (a._isStreaming && !b._isStreaming) return -1;
      if (!a._isStreaming && b._isStreaming) return 1;
      
      // Tiebreaker: optimistic messages go last
      if (a._isOptimistic && !b._isOptimistic) return 1;
      if (!a._isOptimistic && b._isOptimistic) return -1;
      
      // Final tiebreaker: ID (stable ordering)
      const ida = a?.id || '';
      const idb = b?.id || '';
      return ida.localeCompare(idb);
    });
  }, [serverMessages, streamingMessage, optimisticMessage, effectiveChatId]);

  // Collect all objective match IDs from all messages to fetch statuses once (deduplicate API calls)
  const allObjectiveMatchIds = useMemo(() => {
    const ids = new Set();

    const collectMatchIds = (card) => {
      if (!card) return;

      // Single match cards
      if (card.type === 'objective_match' && Array.isArray(card.actions)) {
        card.actions.forEach(action => {
          if (action?.matchId) ids.add(action.matchId);
        });
      }

      // Multi-match cards contain nested cards under `matches`
      if (card.type === 'multi_objective_match' && Array.isArray(card.matches)) {
        card.matches.forEach(nestedCard => collectMatchIds(nestedCard));
      }
    };

    // Check all messages for objective match cards
    if (Array.isArray(allMessages)) {
      allMessages.forEach(message => {
        const entryCards = extractCardsFromEntry(messageCards.get(message.id));
        const cards = entryCards.length > 0
          ? entryCards
          : (Array.isArray(message?.message_metadata?.interactive_cards) ? message.message_metadata.interactive_cards : []);
        cards.forEach(collectMatchIds);
      });
    }

    return Array.from(ids);
  }, [allMessages, messageCards]);

  // Fetch objective match statuses once for all messages (deduplicated)
  const [objectiveMatchStatuses, setObjectiveMatchStatuses] = useState({});
  useEffect(() => {
    let isCancelled = false;
    const fetchStatuses = async () => {
      if (!allObjectiveMatchIds || allObjectiveMatchIds.length === 0) {
        setObjectiveMatchStatuses({});
        return;
      }
      try {
        const res = await objectiveMatchAPI.getStatuses(allObjectiveMatchIds);
        const data = res?.data?.data || {};
        if (!isCancelled) {
          setObjectiveMatchStatuses(data);
        }
      } catch (err) {
        // Silent fail; keep UI interactive
        if (!isCancelled) {
          setObjectiveMatchStatuses({});
        }
      }
    };
    fetchStatuses();
    return () => { isCancelled = true; };
  }, [allObjectiveMatchIds]);

  useEffect(() => {
    if (!Array.isArray(allMessages) || allMessages.length === 0) return;
    const last = getMessageTimestamp(allMessages[allMessages.length - 1]);
    if (last > latestChronologicalTimestampRef.current) {
      latestChronologicalTimestampRef.current = last;
    }
  }, [allMessages]);

  useEffect(() => {
    latestChronologicalTimestampRef.current = 0;
    optimisticTimestampMapRef.current.clear();
    setOptimisticMessage(null);
  }, [effectiveChatId]);


  // Auto-load earlier messages on scroll-up near top (after hooks initialized)
  useEffect(() => {
    const el = containerRef?.current;
    if (!el || isReadOnly) return;
    let lastTriggeredAt = 0;
    const MIN_INTERVAL_MS = 400;
    const onScrollTopLoad = () => {
      try {
        const now = Date.now();
        if (el.scrollTop <= 60 && hasMore && !isLoadingMore) {
          if (now - lastTriggeredAt >= MIN_INTERVAL_MS) {
            lastTriggeredAt = now;
            loadMore(containerRef);
          }
        }
      } catch (_) {}
    };
    el.addEventListener('scroll', onScrollTopLoad, { passive: true });
    return () => {
      try { el.removeEventListener('scroll', onScrollTopLoad); } catch (_) {}
    };
  }, [isReadOnly, containerRef, hasMore, isLoadingMore, loadMore]);

  // WebSocket connection with streaming handlers (disabled for read-only)
  const {
    isConnected,
    sendMessage: sendWebSocketMessage,
    streamingMessage: wsStreamingMessage,
    registerHandler
  } = useUnifiedWebSocket(isReadOnly ? null : effectiveChatId);

  // Welcome message handling with fallbacks (disabled in read-only mode)
  const {
    isWaitingForWelcome,
    welcomeTimeout,
    retryWelcomeMessage,
    clearWelcomeTimeout
  } = useWelcomeMessage(isReadOnly ? null : effectiveChatId, effectiveProjectId, isReadOnly ? false : isConnected);

  // Track when the websocket first becomes connected for readiness gating
  useEffect(() => {
    if (isConnected) {
      if (!connectedAtRef.current) {
        connectedAtRef.current = Date.now();
      }
    } else {
      connectedAtRef.current = null;
    }
  }, [isConnected]);

  // Update local streaming message state from WebSocket
  useEffect(() => {
    if (wsStreamingMessage && wsStreamingMessage.id) {
      setStreamingMessage(wsStreamingMessage);
      // if (process.env.NODE_ENV !== 'production') {
      //   try { console.debug('[msgs] streaming update', { id: wsStreamingMessage.id, len: (wsStreamingMessage.content || '').length }); } catch (_) {}
      // }
    } else if (wsStreamingMessage && wsStreamingMessage.isComplete) {
      // Clear streaming message when complete
      setStreamingMessage(null);
      // if (process.env.NODE_ENV !== 'production') {
      //   try { console.debug('[msgs] streaming complete'); } catch (_) {}
      // }
    }
  }, [wsStreamingMessage]);

  // Track latest streaming message id for migration logic
  useEffect(() => {
    if (streamingMessage && streamingMessage.id) {
      streamingIdRef.current = streamingMessage.id;
      // if (process.env.NODE_ENV !== 'production') {
      //   try { console.debug('[msgs] set streamingIdRef', { id: streamingMessage.id }); } catch (_) {}
      // }
    }
  }, [streamingMessage]);

  // RACE CONDITION FIX: Removed proactive refetch that was causing message reordering
  // Instead, rely on user_message_saved/ai_message_saved events to append/replace messages
  // This prevents the race between streaming, refetch, and optimistic message insertion
  // useEffect(() => {
  //   const newId = wsStreamingMessage?.id;
  //   const prevId = streamingIdRef.current;
  //   if (!newId || !prevId || newId === prevId) return;
  //   const prevExists = Array.isArray(allMessages) && allMessages.some(m => m && m.id === prevId);
  //   if (!prevExists && effectiveChatId) {
  //     try {
  //       queryClient.invalidateQueries({ queryKey: createQueryKey.chat(effectiveChatId) });
  //       if (process.env.NODE_ENV !== 'production') {
  //         try { console.debug('[msgs] invalidated chat to pull previous streaming', { prevId, newId }); } catch (_) {}
  //       }
  //     } catch (_) {}
  //   }
  // }, [wsStreamingMessage?.id, allMessages, effectiveChatId, queryClient]);

  // WebSocket message handlers
  useEffect(() => {
    if (!registerHandler) return undefined;

    const disposers = [];
    const trackHandler = (type, handler) => {
      const dispose = registerHandler(type, handler);
      if (typeof dispose === 'function') {
        disposers.push(dispose);
      }
    };

    const onUserMessageSaved = (data) => {
      try {
        if (!data?.message_id) return;

        const clientMessageId = data?.client_message_id;
        const storedOptimisticTs = clientMessageId ? optimisticTimestampMapRef.current.get(clientMessageId) : null;
        const optimisticTimestamp = storedOptimisticTs || optimisticMessageRef.current?.createdAt;
        const createdAt = optimisticTimestamp || data.timestamp || new Date().toISOString();

        // Update optimistic message ID immediately to match server ID
        // This ensures React uses the same key and doesn't remount the component
        // Then clear it since the server message will replace it
        if (clientMessageId && optimisticMessageRef.current?.client_message_id === clientMessageId) {
          setOptimisticMessage((current) => {
            if (!current || current.client_message_id !== clientMessageId) return current;
            // Update ID to server ID so React key stays stable, then clear immediately
            // The server message will seamlessly replace it
            return null;
          });
        } else if (!clientMessageId) {
          setOptimisticMessage(null);
        }

        // Add the real message (addMessage will deduplicate by client_message_id)
        addMessage({
          id: data.message_id,
          role: 'user',
          content: data.content || '',
          createdAt,
          chatId: effectiveChatId,
          message_metadata: data.metadata || {},
          client_message_id: clientMessageId
        });

        latestChronologicalTimestampRef.current = Math.max(
            latestChronologicalTimestampRef.current,
            getMessageTimestamp({ createdAt })
        );

        // Clean up timestamp map
        if (clientMessageId) {
          optimisticTimestampMapRef.current.delete(clientMessageId);
        }
      } catch (_) {}
    };
    trackHandler('user_message_saved', onUserMessageSaved);

    const onAiMessageSaved = (data) => {
      setIsGenerating(false);
      try { clearWelcomeTimeout && clearWelcomeTimeout(); } catch (_) {}
      setIsReplacingStreamingMessage(true);
      setScrollLocked(true);

      const aiTimestampMs = data?.timestamp ? new Date(data.timestamp).getTime() : Date.now();
      const safeTimestampMs = Math.max(aiTimestampMs, latestChronologicalTimestampRef.current + 1);
      const createdAt = new Date(safeTimestampMs).toISOString();

      try {
        if (data?.message_id) {
          addMessage({
            id: data.message_id,
            role: 'assistant',
            content: data.content || '',
            createdAt,
            chatId: effectiveChatId,
            message_metadata: data.metadata || {}
          });
          latestChronologicalTimestampRef.current = Math.max(
            latestChronologicalTimestampRef.current,
            safeTimestampMs
          );
        }
      } catch (_) {}

      // ✅ FIX: Populate messageCards immediately if metadata has them
      // This fixes the "Spinner never disappears" issue by ensuring we don't wait for cards that are already here
      try {
        if (data?.message_id && data?.metadata?.interactive_cards && Array.isArray(data.metadata.interactive_cards)) {
           const cards = data.metadata.interactive_cards;
           if (cards.length > 0) {
             const incomingVersion = typeof data.metadata.interactive_cards_version === 'number'
              ? data.metadata.interactive_cards_version
              : 1;
             const status = data?.metadata?.response_status || 'final';
             setChatState(prev => {
                const prevEntry = prev.messageCards.get(data.message_id);
                if (extractVersionFromEntry(prevEntry) >= incomingVersion) {
                  return prev;
                }
                const updated = new Map(prev.messageCards);
                updated.set(data.message_id, createCardEntry(cards, incomingVersion, status));
                return { ...prev, messageCards: updated };
             });
           }
        }
      } catch (_) {}

      try {
        const streamingId = streamingIdRef.current;
        const savedId = data?.message_id;
        if (streamingId && savedId && streamingId !== savedId) {
          setChatState(prev => {
            const migrated = new Map(prev.messageCards);
            if (migrated.has(streamingId)) {
              const cardsForStream = migrated.get(streamingId);
              if (cardsForStream && !migrated.has(savedId)) {
                migrated.set(savedId, cardsForStream);
              }
              migrated.delete(streamingId);
              return { ...prev, messageCards: migrated };
            }
            return prev;
          });
        }
      } catch (_) {}

      try {
        if (data?.message_id) {
          setAwaitingCardsMessageId(data.message_id);
          setTimeout(() => {
            setAwaitingCardsMessageId(prev => {
              if (prev === data.message_id && !messageCardsRef.current?.has(data.message_id)) {
                return null;
              }
              return prev;
            });
          }, 1200);
        }
      } catch (_) {}

      if (fallbackTimeoutRef.current) {
        clearTimeout(fallbackTimeoutRef.current);
      }
      fallbackTimeoutRef.current = setTimeout(() => {
        setStreamingMessage(null);
        setIsReplacingStreamingMessage(false);
        setScrollLocked(false);
        fallbackTimeoutRef.current = null;
      }, 3000);
    };
    trackHandler('ai_message_saved', onAiMessageSaved);

    const onGenerationStopped = () => {
      setIsGenerating(false);
      setStreamingMessage(null);
    };
    trackHandler('generation_stopped', onGenerationStopped);

    const onInteractiveCards = (data) => {
      if (!data?.message_id || !Array.isArray(data?.cards)) return;
      const incomingVersion = typeof data.version === 'number' ? data.version : 1;
      const status = data?.status || 'final';
      setChatState(prev => {
        const existing = prev.messageCards.get(data.message_id);
        if (extractVersionFromEntry(existing) >= incomingVersion) {
          return prev;
        }
        const next = new Map(prev.messageCards);
        next.set(data.message_id, createCardEntry(data.cards, incomingVersion, status));
        return { ...prev, messageCards: next };
      });
    };
    const onCroppedImagesReady = (data) => {
      if (data?.message_id && data?.crops) {
        setChatState(prev => ({
          ...prev,
          croppedImages: new Map(prev.croppedImages).set(data.message_id, data.crops)
        }));
      }
    };
    trackHandler('interactive_cards', onInteractiveCards);
    trackHandler('cropped_images_ready', onCroppedImagesReady);

    const onObjectiveProgress = async (data) => {
      if (data?.confirmedBy) {
        const objectiveTitle = data?.objectiveTitle || 'an objective';
        const progressCount = data?.progressCount || 0;
        const targetCount = data?.targetCount || 1;
        const dedupeKey = `${data?.objectiveId || objectiveTitle}:${data?.confirmedBy?.id}:${progressCount}`;
        setChatState(prev => {
          const alreadyQueued = prev.objectiveNotifications.some(n => n._key === dedupeKey);
          if (alreadyQueued) return prev;
          return {
            ...prev,
            objectiveNotifications: [
              ...prev.objectiveNotifications,
              {
                user: data.confirmedBy,
                action: 'objective_fulfilled',
                objectiveTitle,
                objectiveProgress: `${progressCount}/${targetCount}`,
                id: `obj-${Date.now()}-${Math.random().toString(36).slice(2,8)}`,
                _key: dedupeKey
              }
            ]
          };
        });

        if (effectiveProjectId && queryClient) {
          // Invalidate and refetch project query to update objectives list with fresh progressCount
          await queryClient.invalidateQueries(createQueryKey.project(effectiveProjectId));
          await queryClient.refetchQueries(createQueryKey.project(effectiveProjectId));
        }
      }
    };
    trackHandler('objective_progress', onObjectiveProgress);

    const onError = (data) => {
      console.error('WebSocket error:', data);
      toast.error(data?.message || 'An error occurred');
      setIsGenerating(false);
      setStreamingMessage(null);
    };
    trackHandler('error', onError);

    return () => {
      disposers.forEach((dispose) => {
        try { dispose(); } catch (_) {}
      });
    };
  }, [registerHandler, effectiveChatId, clearWelcomeTimeout, addMessage, queryClient, effectiveProjectId]);

  // Compass heading is now managed by the shared useCompass hook
  // No need for duplicate listener setup here

  // 🗺️ Get user location for AI suggestions
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          // Include compass heading from Device Orientation API (works even when stationary)
          if (compassHeadingRef.current !== null && typeof compassHeadingRef.current === 'number') {
            location.heading = compassHeadingRef.current;
          }
          // Fallback to geolocation heading if compass not available (only when device is moving)
          else if (typeof position.coords.heading === 'number' && !Number.isNaN(position.coords.heading) && position.coords.heading >= 0) {
            location.heading = ((position.coords.heading % 360) + 360) % 360;
          }
          setChatState(prev => ({
            ...prev,
            userLocation: location
          }));
          // console.log('📍 Got user location for AI suggestions:', position.coords.latitude, position.coords.longitude);
        },
        (error) => {
          console.warn('📍 map for AI suggestions:', error.code, error.message);
        },
        {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 600000 // 10 minute cache
        }
      );
    }
  }, []);

  // Chat queries are handled by usePaginatedMessages hook
  // No need for duplicate chat query

  const { data: chatsData, isLoading: isChatsLoading } = useQuery({
    queryKey: createQueryKey.chats(effectiveProjectId),
    queryFn: () => chatAPI.getByProject(effectiveProjectId),
    enabled: !!effectiveProjectId,
    staleTime: 30000, // Optimized: Consider data fresh for 30s since chat list rarely changes
    refetchOnWindowFocus: false, // Optimized: Disable aggressive refetching for chat list
    refetchOnMount: true, // ✅ STALE DATA FIX: Always refetch when revisiting
    select: (response) => response.data?.data?.chats || []
  });

  

  // Notify parent component of messages changes for useMessagePins
  useEffect(() => {
    if (onMessagesChange && allMessages) {
      onMessagesChange(allMessages);
    }
  }, [allMessages, onMessagesChange]);

  // Fullscreen image viewer state
  const [viewerOpen, setViewerOpen] = useState(false);
  // External command for IsolatedChatInput (to support chatless camera flows)
  const [externalCommand, setExternalCommand] = useState(null);

  const [viewerImageUrl, setViewerImageUrl] = useState('');
  const [viewerSpotlights, setViewerSpotlights] = useState([]);
  const [highlightedSpotlightId, setHighlightedSpotlightId] = useState(null);

  const openViewerForMessage = useCallback(({ message }) => {
    const imageUrl = message?.message_metadata?.imageUrl;
    if (!imageUrl) return;

    // First try: backlink via assistant metadata
    let associatedAssistantMessageId = null;
    if (allMessages && allMessages.length > 0) {
      const userMessageId = message.id;
      const assistantForThisImage = allMessages.find(m => {
        if (m.role !== 'assistant') return false;
        const meta = m?.message_metadata || {};
        return (meta.source_user_message_id === userMessageId) || (meta.source_image_url && meta.source_image_url === imageUrl);
      });
      if (assistantForThisImage) {
        associatedAssistantMessageId = assistantForThisImage.id;
      }
    }

    // Fallback heuristic: next assistant message after this user message that has spotlights
    if (!associatedAssistantMessageId && allMessages && allMessages.length > 0) {
      const idx = allMessages.findIndex(m => m.id === message.id);
      if (idx !== -1) {
        for (let i = idx + 1; i < allMessages.length; i++) {
          const candidate = allMessages[i];
          if (candidate?.role === 'assistant' && messageCards?.has(candidate.id)) {
            const cards = extractCardsFromEntry(messageCards.get(candidate.id));
            if (cards.some(c => c.type === 'spotlight')) {
              associatedAssistantMessageId = candidate.id;
              break;
            }
          }
        }
      }
    }

    // Fallback: if nothing found in history, use streaming assistant message (if present with spotlights)
    if (!associatedAssistantMessageId && streamingMessage?.id && messageCards?.has(streamingMessage.id)) {
      const cards = extractCardsFromEntry(messageCards.get(streamingMessage.id));
      if (cards.some(c => c.type === 'spotlight')) {
        associatedAssistantMessageId = streamingMessage.id;
      }
    }

    // Extract spotlights only from the associated assistant message
    let spotlights = [];
    if (associatedAssistantMessageId && messageCards?.has(associatedAssistantMessageId)) {
      const cards = extractCardsFromEntry(messageCards.get(associatedAssistantMessageId));
      spotlights = cards.filter(c => c.type === 'spotlight');
      
      // Debug logging for spotlight bbox investigation
      if (process.env.NODE_ENV !== 'production' && spotlights.length > 0) {
        console.debug('[ChatSection] Spotlight data for viewer:', {
          messageId: associatedAssistantMessageId,
          spotlights: spotlights.map(s => ({
            id: s.id,
            title: s.title,
            bounding_box: s.bounding_box,
            bounding_boxes: s.bounding_boxes
          }))
        });
      }
    }

    // console.log('[ChatSection] Opening viewer with:', {
    //   imageUrl,
    //   spotlightsCount: spotlights.length,
    //   spotlights: spotlights.map(s => ({ id: s.id, title: s.title, bbox: s.bounding_box }))
    // });
    
    setViewerImageUrl(imageUrl);
    setViewerSpotlights(spotlights);
    setViewerOpen(true);
  }, [allMessages, messageCards, streamingMessage]);

  const closeViewer = useCallback(() => {
    setViewerOpen(false);
    setViewerImageUrl('');
    setViewerSpotlights([]);
    setHighlightedSpotlightId(null);
  }, []);

  // Memoized message processing to avoid unnecessary reprocessing
  const processedMessages = useMemo(() => {
    if (!allMessages || allMessages.length === 0) {
      return { newCardsMap: new Map(), newCroppedImagesMap: new Map() };
    }
    
    const newCardsMap = new Map();
    const newCroppedImagesMap = new Map();
    
    allMessages.forEach(message => {
      // Check if message has interactive cards in metadata
      const cards = message?.message_metadata?.interactive_cards;
      if (cards && Array.isArray(cards) && cards.length > 0) {
        // Ensure learn_content is included in cards for persistence
        const enrichedCards = cards.map(card => ({
          ...card,
          learn_content: card.learn_content || null
        }));

        const version = typeof message?.message_metadata?.interactive_cards_version === 'number'
          ? message.message_metadata.interactive_cards_version
          : 1;
        const status = message?.message_metadata?.response_status || 'final';
        newCardsMap.set(message.id, createCardEntry(enrichedCards, version, status));
      }
      
      // Check if message has cropped images in metadata (LEGACY)
      const croppedImagesData = message?.message_metadata?.cropped_images;
      if (croppedImagesData && typeof croppedImagesData === 'object') {
        newCroppedImagesMap.set(message.id, croppedImagesData);
      }
    });
    
    return { newCardsMap, newCroppedImagesMap };
  }, [allMessages]);

  // Resolve the correct source image URL for a given assistant message id
  const getSourceImageForAssistantMessage = useCallback((assistantMessageId) => {
    try {
      if (!assistantMessageId || !Array.isArray(allMessages)) return null;
      const assistantMsg = allMessages.find(m => m && m.id === assistantMessageId);
      const aMeta = assistantMsg?.message_metadata || {};
      if (aMeta.source_image_url) {
        return aMeta.source_image_url;
      }
      if (aMeta.source_user_message_id) {
        const userMsg = allMessages.find(m => m && m.id === aMeta.source_user_message_id && m.role === 'user');
        const url = userMsg?.message_metadata?.imageUrl;
        if (url) return url;
      }
      const idx = allMessages.findIndex(m => m && m.id === assistantMessageId);
      if (idx > 0) {
        for (let i = idx - 1; i >= 0; i--) {
          const candidate = allMessages[i];
          const url = candidate?.role === 'user' ? candidate?.message_metadata?.imageUrl : null;
          if (url) return url;
        }
      }
      const recentUserWithImage = [...allMessages].reverse().find(m => m?.role === 'user' && m?.message_metadata?.imageUrl);
      return recentUserWithImage?.message_metadata?.imageUrl || null;
    } catch (_) {
      return null;
    }
  }, [allMessages]);

  // Helper function for shallow comparison of arrays/objects
  const shallowEqual = useCallback((a, b) => {
    if (a === b) return true;
    if (!a || !b) return false;
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      return a.every((item, index) => item === b[index]);
    }
    if (typeof a === 'object' && typeof b === 'object') {
      const keysA = Object.keys(a);
      const keysB = Object.keys(b);
      if (keysA.length !== keysB.length) return false;
      return keysA.every(key => a[key] === b[key]);
    }
    return false;
  }, []);

  // Extract cards, cropped images, and bounding boxes from loaded messages and populate state
  useEffect(() => {
    const { newCardsMap, newCroppedImagesMap } = processedMessages;
    
    // Update messageCards with a merge strategy to avoid flicker when websocket-provided cards arrive before persistence
    if (newCardsMap.size > 0 || messageCards.size > 0) {
      setChatState(prev => {
        const merged = new Map(prev.messageCards);
        let changed = false;
        for (let [key, value] of newCardsMap) {
          const prevVal = merged.get(key);
          const incomingVersion = extractVersionFromEntry(value);
          const currentVersion = extractVersionFromEntry(prevVal);
          if (incomingVersion > currentVersion) {
            merged.set(key, value);
            changed = true;
          }
        }
        if (changed) {
          return { ...prev, messageCards: merged };
        }
        return prev;
      });
    }
    
    // Update croppedImages if there are changes
    if (newCroppedImagesMap.size > 0 || croppedImages.size > 0) {
      setChatState(prev => {
        // Check if maps are different
        if (prev.croppedImages.size !== newCroppedImagesMap.size) {
          return { ...prev, croppedImages: newCroppedImagesMap };
        }
        
        // Check if content is different using shallow comparison
        for (let [key, value] of newCroppedImagesMap) {
          if (!prev.croppedImages.has(key) || !shallowEqual(prev.croppedImages.get(key), value)) {
            return { ...prev, croppedImages: newCroppedImagesMap };
          }
        }
        
        return prev; // No changes
      });
    }
  }, [processedMessages, messageCards.size, croppedImages.size, shallowEqual]);

  // Debug: Track changes to allMessages
  // useEffect(() => {
  //   console.log('🔍 allMessages changed:', {
  //     count: allMessages?.length || 0,
  //     messages: allMessages?.map(m => ({ id: m.id, role: m.role, content: m.content?.substring(0, 50) + '...' })) || []
  //   });
  // }, [allMessages]);

  // Infinite scroll for loading more messages is handled by usePaginatedMessages hook
  // No need for duplicate useInfiniteScroll hook

  // Auto-scroll effects
  // 1. Auto-scroll on initial load when messages are first loaded
  // Track if we've done initial scroll for this chat
  const hasInitialScrolledRef = useRef(false);
  
  useEffect(() => {
    // Reset scroll flag when chat changes
    hasInitialScrolledRef.current = false;
  }, [effectiveChatId]);
  
  useEffect(() => {
    if (!disableAutoScroll && allMessages && allMessages.length > 0 && !hasInitialScrolledRef.current) {
      // console.log('🚀 Auto-scroll: Initial load with', allMessages.length, 'messages');
      hasInitialScrolledRef.current = true;
      
      // Use a small delay to ensure DOM is fully rendered, especially for images
      setTimeout(() => {
        if (autoScrollOnInitialLoad) {
          autoScrollOnInitialLoad();
        }
      }, 50);
    }
  }, [allMessages, autoScrollOnInitialLoad, disableAutoScroll]);

  // 2. Auto-scroll when optimistic message is added (user sends message)
  // Track if last message had an image to trigger additional scroll attempts
  const lastMessageHadImageRef = useRef(false);
  
  useEffect(() => {
    if (!disableAutoScroll && optimisticMessage) {
      // console.log('🚀 Auto-scroll: Optimistic message added');
      
      // Check if this message has an image
      const hasImage = optimisticMessage?.metadata?.imageUrl || optimisticMessage?.message_metadata?.imageUrl;
      lastMessageHadImageRef.current = hasImage;
      
      if (autoScrollOnNewContent) {
        autoScrollOnNewContent('smooth');
        
        // For images, add additional scroll attempts as they load
        if (hasImage) {
          // console.log('🖼️ Message has image, scheduling additional scroll attempts');
          setTimeout(() => {
            if (autoScrollOnNewContent && !userHasScrolled) {
              autoScrollOnNewContent('smooth');
            }
          }, 150);
          
          setTimeout(() => {
            if (autoScrollOnNewContent && !userHasScrolled) {
              autoScrollOnNewContent('smooth');
            }
          }, 400);
          
          // Final attempt after image should be loaded
          setTimeout(() => {
            if (autoScrollOnNewContent && !userHasScrolled) {
              autoScrollOnNewContent('smooth');
            }
          }, 800);
        }
      }
    }
  }, [optimisticMessage, autoScrollOnNewContent, disableAutoScroll, userHasScrolled]);

  // 3. Auto-scroll during streaming (when content is being generated)
  useEffect(() => {
    if (!disableAutoScroll && streamingMessage?.content && !isReplacingStreamingMessage && !userHasScrolled) {
      // console.log('🚀 Auto-scroll: Streaming content update, length:', streamingMessage.content.length);
      if (autoScrollOnStreaming) {
        autoScrollOnStreaming();
      }
    }
  }, [streamingMessage?.content, autoScrollOnStreaming, isReplacingStreamingMessage, disableAutoScroll, userHasScrolled]);

  // 4. Auto-scroll when generation state changes (loading indicator appears/disappears)
  useEffect(() => {
    if (!disableAutoScroll && isGenerating && !userHasScrolled) {
      // console.log('🚀 Auto-scroll: Generation started, showing loading indicator');
      if (autoScrollOnNewContent) {
        // Small delay to ensure the loading indicator has rendered
        setTimeout(() => {
          if (!userHasScrolled) {
            // console.log('🚀 Auto-scroll: Delayed scroll for loading indicator');
            autoScrollOnNewContent('smooth');
          }
        }, 100);
      }
    }
  }, [isGenerating, autoScrollOnNewContent, disableAutoScroll, userHasScrolled]);

  // 5. Force auto-scroll when chat ID changes (switching chats)
  useEffect(() => {
    if (!disableAutoScroll && effectiveChatId && resetScrollState) {
      // console.log('🚀 Auto-scroll: Chat changed, resetting scroll state');
      resetScrollState();
      // Also reset the initial scroll flag for the new chat
      hasInitialScrolledRef.current = false;
    }
  }, [effectiveChatId, resetScrollState, disableAutoScroll]);

  // 6. Clear streaming message only when final message appears in allMessages (with timeout safety)
  useEffect(() => {
    if (!disableAutoScroll && isReplacingStreamingMessage && streamingMessage?.id && allMessages) {
      const finalMessageExists = allMessages.find(msg => msg.id === streamingMessage.id);
      if (finalMessageExists) {
        // console.log('🔄 Final message now rendered, clearing streaming message');
        
        // Clear the fallback timeout since we found the final message
        if (fallbackTimeoutRef.current) {
          clearTimeout(fallbackTimeoutRef.current);
          fallbackTimeoutRef.current = null;
        }
        
        // Preserve scroll position during transition
        const preserveScrollPosition = () => {
          if (containerRef.current) {
            const container = containerRef.current;
            const scrollBefore = container.scrollTop;
            const scrollHeightBefore = container.scrollHeight;
            
            // Clear streaming message
            setStreamingMessage(null);
            
            // Use multiple RAF to ensure DOM is updated
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                if (container) {
                  const scrollHeightAfter = container.scrollHeight;
                  const heightDifference = scrollHeightAfter - scrollHeightBefore;
                  
                  // Adjust scroll position to compensate for any height changes
                  container.scrollTop = scrollBefore + heightDifference;
                  
                  // Clear replacement flag and scroll lock after a longer delay
                  setTimeout(() => {
                    setIsReplacingStreamingMessage(false);
                    setScrollLocked(false);
                    // Only do final scroll if we're near the bottom
                    if (container.scrollTop + container.clientHeight >= container.scrollHeight - 50) {
                      container.scrollTop = container.scrollHeight;
                    }
                  }, 200); // Increased delay to prevent competing scrolls
                }
              });
            });
          } else {
            // Fallback if no container
            setStreamingMessage(null);
            setTimeout(() => {
              setIsReplacingStreamingMessage(false);
              setScrollLocked(false);
            }, 200);
          }
        };
        
        preserveScrollPosition();
      }
      // Safety timeout: if final message never appears, clear replacement state to avoid stuck spinner
      const safety = setTimeout(() => {
        setIsReplacingStreamingMessage(false);
        setScrollLocked(false);
      }, 2000);
      return () => clearTimeout(safety);
    }
  }, [isReplacingStreamingMessage, streamingMessage?.id, allMessages, containerRef, disableAutoScroll]);

  // 6b. When cards arrive for the current streaming or just-saved assistant message, nudge to bottom if near bottom
  useEffect(() => {
    if (disableAutoScroll) return;
    const currentAssistantId = streamingMessage?.id || awaitingCardsMessageId;
    if (!currentAssistantId) return;
    if (!messageCards.has(currentAssistantId)) return;
    if (userHasScrolled || isReplacingStreamingMessage) return;
    if (typeof isNearBottom === 'function' && !isNearBottom()) return;
    if (typeof autoScrollOnStreamingComplete === 'function') {
      setTimeout(() => {
        autoScrollOnStreamingComplete();
      }, 50);
    } else if (typeof scrollToBottom === 'function') {
      setTimeout(() => scrollToBottom('smooth'), 50);
    }
  }, [disableAutoScroll, messageCards, streamingMessage?.id, awaitingCardsMessageId, userHasScrolled, isReplacingStreamingMessage, isNearBottom, autoScrollOnStreamingComplete, scrollToBottom]);


  // 7. Cleanup effect
  useEffect(() => {
    // Capture refs at effect creation time to avoid stale references
    const fallbackTimeoutRef_current = fallbackTimeoutRef.current;
    const scrollLockTimeoutRef_current = scrollLockTimeoutRef.current;
    const welcomeTimeoutRef_current = welcomeTimeoutRef.current;
    
    return () => {
      // Clear timeouts on unmount using captured values
      if (fallbackTimeoutRef_current) {
        clearTimeout(fallbackTimeoutRef_current);
      }
      if (scrollLockTimeoutRef_current) {
        clearTimeout(scrollLockTimeoutRef_current);
      }
      if (welcomeTimeoutRef_current) {
        clearTimeout(welcomeTimeoutRef_current);
      }
    };
  }, []);

  // Client-side chat creation removed; server creates chat on project creation

  const deleteChatMutation = useMutation({
    mutationFn: (chatId) => chatAPI.deleteChat(chatId),
    onSuccess: (_, deletedChatId) => {
      queryClient.invalidateQueries({ queryKey: ['chats', effectiveProjectId] });
      if (deletedChatId === effectiveChatId) {
        navigate(`/projects/${effectiveProjectId}`);
        if (effectiveSetActiveChatId && typeof effectiveSetActiveChatId === 'function') {
          effectiveSetActiveChatId(null);
        }
      }
    }
  });

  // Handlers
  const handleSendMessage = useCallback(async (message, imageUrl = null, extraMetadata = null) => {
    // Disable all interactions in read-only mode
    if (isReadOnly) {
      return;
    }
    
    if (!effectiveChatId || !isConnected) {
      toast.error('Not connected to chat');
      return;
    }
    let clientMessageId = null;

    try {
      // Clear ALL AI pins (temp AND persisted) when a new message is sent
      // BUT: Don't clear pins when selecting a location card (we just set them!)
      const isLocationCardSelection = extraMetadata?.selected_location_card === true;
      try { 
        if (!isLocationCardSelection) {
          onAISuggestionsChange && onAISuggestionsChange([]); 
          onPersistLocationPin && onPersistLocationPin(null); // Clear persisted pins too
        }
      } catch (_) {}
      // Prepare base message metadata
      const metadata = { ...(extraMetadata || {}) };
      
      // console.log('🔍 DEBUG: Starting message send process...');
      
      // Add image URL if provided
      if (imageUrl) {
        metadata.imageUrl = imageUrl;
        // console.log('🔍 DEBUG: Added image URL to metadata');
      }

      // Create client-side correlation ID for optimistic reconciliation
      clientMessageId = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? `c_${crypto.randomUUID()}`
        : `c_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

      const optimisticBaseMs = Math.max(
        Date.now(),
        lastPersistedServerTimestamp || 0,
        latestChronologicalTimestampRef.current || 0
      );
      const optimisticCreatedAt = new Date(optimisticBaseMs + OPTIMISTIC_TIMESTAMP_BUFFER_MS).toISOString();

      // Create optimistic message to show immediately
      const optimisticMsg = {
        id: `optimistic-${Date.now()}`,
        role: 'user',
        content: message,
        createdAt: optimisticCreatedAt,
        chatId: effectiveChatId,
        isOptimistic: true,
        client_message_id: clientMessageId,
        metadata
      };

      // Show optimistic message immediately
      setOptimisticMessage(optimisticMsg);
      optimisticTimestampMapRef.current.set(clientMessageId, optimisticCreatedAt);
      // console.log('🟢 Optimistic message created:', optimisticMsg.id, 'Content:', optimisticMsg.content);

      // Start generating state immediately
      setIsGenerating(true);
      
      // If this is an image message, trigger immediate scroll
      if (imageUrl && forceScrollToBottom) {
        setTimeout(() => {
          forceScrollToBottom();
        }, 100);
      }

      // Attempt to fetch location without blocking
      const tryGetLocationQuickly = async () => {
        if (!navigator.geolocation) return null;
        try {
          const geoPromise = new Promise((resolve) => {
            navigator.geolocation.getCurrentPosition(
              (pos) => {
                const location = { lat: pos.coords.latitude, lng: pos.coords.longitude };
                // Prefer compass heading from Device Orientation API (works even when stationary)
                if (compassHeadingRef.current !== null && typeof compassHeadingRef.current === 'number') {
                  location.heading = compassHeadingRef.current;
                }
                // Fallback to geolocation heading if compass not available (only when device is moving)
                else if (typeof pos.coords.heading === 'number' && !Number.isNaN(pos.coords.heading) && pos.coords.heading >= 0) {
                  location.heading = ((pos.coords.heading % 360) + 360) % 360;
                }
                resolve(location);
              },
              () => resolve(null),
              {
                enableHighAccuracy: false,
                timeout: 5000,
                maximumAge: 300000
              }
            );
          });
          const timeoutPromise = new Promise((resolve) => setTimeout(() => resolve('timeout'), 300));
          const result = await Promise.race([geoPromise, timeoutPromise]);
          if (result && result !== 'timeout') {
            return result;
          }
          return null;
        } catch (_) {
          return null;
        }
      };

      let location = null;
      try {
        location = await tryGetLocationQuickly();
      } catch (_) {}
      // Fallback to last-known userLocation from state (non-blocking)
      if (!location && userLocation && typeof userLocation.lat === 'number' && typeof userLocation.lng === 'number') {
        location = { lat: userLocation.lat, lng: userLocation.lng };
        // Include heading from userLocation if available (prefer compass, fallback to geolocation)
        if (compassHeadingRef.current !== null && typeof compassHeadingRef.current === 'number') {
          location.heading = compassHeadingRef.current;
        } else if (typeof userLocation.heading === 'number' && !Number.isNaN(userLocation.heading)) {
          location.heading = userLocation.heading;
        }
      }
      // If we have location but no heading, try to get compass heading
      if (location && !location.heading && compassHeadingRef.current !== null && typeof compassHeadingRef.current === 'number') {
        location.heading = compassHeadingRef.current;
      }
      if (location) {
        metadata.location = location;
      }

      // Send the message via WebSocket (with whatever metadata we have now)
      const messageData = {
        type: 'chat_message',
        data: {
          content: message,
          chatId: effectiveChatId,
          projectId: effectiveProjectId,
          client_message_id: clientMessageId,
          metadata
        }
      };
      await sendWebSocketMessage(messageData);

    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
      optimisticTimestampMapRef.current.delete(clientMessageId);
      // Clear optimistic message on error
      setOptimisticMessage(null);
    }
  }, [isReadOnly, effectiveChatId, isConnected, sendWebSocketMessage, effectiveProjectId, forceScrollToBottom, userLocation, onAISuggestionsChange, onPersistLocationPin, lastPersistedServerTimestamp]);

  // Retry handler for error assistant messages – resend the last user message before the error
  const handleRetryMessage = useCallback((errorMessage) => {
    try {
      if (!errorMessage || !Array.isArray(allMessages) || allMessages.length === 0) {
        return;
      }

      // Find the index of the error message in the unified message list
      const idx = allMessages.findIndex(
        (m) => (m?.id && errorMessage.id && m.id === errorMessage.id)
      );

      let userMessage = null;

      if (idx !== -1) {
        // Walk backwards to find the preceding user message
        for (let i = idx - 1; i >= 0; i -= 1) {
          const candidate = allMessages[i];
          if (candidate && candidate.role === 'user') {
            userMessage = candidate;
            break;
          }
        }
      }

      // Fallback: if we couldn't locate by index, use the most recent user message
      if (!userMessage) {
        for (let i = allMessages.length - 1; i >= 0; i -= 1) {
          const candidate = allMessages[i];
          if (candidate && candidate.role === 'user') {
            userMessage = candidate;
            break;
          }
        }
      }

      if (!userMessage) {
        toast.error('Could not find a message to retry.');
        return;
      }

      const baseContent = userMessage.content || '';
      const meta = userMessage.message_metadata || userMessage.metadata || {};
      const imageUrl = meta.imageUrl || null;

      if (!baseContent && !imageUrl) {
        toast.error('Original message had no content to retry.');
        return;
      }

      handleSendMessage(baseContent, imageUrl, meta);
    } catch (e) {
      console.error('Failed to retry message:', e);
      toast.error('Failed to retry message');
    }
  }, [allMessages, handleSendMessage]);

  // Handle card clicks - send the card's action as a message
  const handleCardClick = useCallback((action, card) => {
    try {
      // Disable all interactions in read-only mode
      if (isReadOnly) {
        return;
      }
      
      // Validate card before processing
      if (!card || !card.id) {
        console.error('Invalid card clicked:', card);
        return;
      }
    // Handle objective match confirm/dismiss inline actions
    if (action && typeof action === 'object') {
      const act = action; // action payload from objective_match
      const type = act?.type;
      const matchId = act?.matchId;
      if ((type === 'confirm_match' || type === 'dismiss_match') && matchId) {
        // Use already-imported APIs directly (no dynamic import needed)
        const confirmOrDismiss = async () => {
          try {
            // Call the confirm/dismiss API
            const apiCall = type === 'confirm_match' 
              ? objectiveMatchAPI.confirm(matchId) 
              : objectiveMatchAPI.dismiss(matchId);
            await apiCall;
            
            // Invalidate and refetch project query to update objectives list
            if (effectiveProjectId && queryClient) {
              console.log('🎯 Objective match confirmed/dismissed, invalidating project query:', effectiveProjectId);
              await queryClient.invalidateQueries({ queryKey: createQueryKey.project(effectiveProjectId) });
              // Refetch immediately to ensure fresh data
              await queryClient.refetchQueries({ queryKey: createQueryKey.project(effectiveProjectId) });
              // Also invalidate the generic projects list
              await queryClient.invalidateQueries({ queryKey: ['projects'] });
            }
            
            // Refetch objective match statuses to update UI immediately
            const idsToFetch = [...new Set([...allObjectiveMatchIds, matchId])];
            if (idsToFetch.length > 0) {
              try {
                const res = await objectiveMatchAPI.getStatuses(idsToFetch);
                const data = res?.data?.data || {};
                setObjectiveMatchStatuses(data);
              } catch (statusErr) {
                console.warn('Failed to fetch match statuses:', statusErr);
              }
            }
            
            // Callback for celebration/UI updates
            if (type === 'confirm_match' && typeof onObjectiveConfirmed === 'function') {
              try { onObjectiveConfirmed(); } catch (_) {}
            }
          } catch (err) {
            console.error('Failed to confirm/dismiss objective match:', err);
          }
        };
        
        confirmOrDismiss();
        return;
      }
      if (type === 'end_quest') {
        // In competitive sessions, ignore local end_quest prompts.
        // The server will broadcast the finish and victory state to all users.
        try {
          if (effectiveProject && effectiveProject.gameMode === 'competitive') {
            return;
          }
        } catch (_) {}
        if (typeof onRequestFinishProject === 'function') {
          try { onRequestFinishProject(); } catch (_) {}
        } else {
          handleSendMessage("Yes, I'm done. Please finish the project now.");
        }
        return;
      }
      if (act?.action) {
        handleSendMessage(act.action);
      }
      return;
    }

    // console.log('🎴 Card clicked:', card.title, card.type === 'location' ? '(location pin)' : `→ ${action}`);
    // For empty actions, provide sensible defaults instead of no-op
    if (!action || (typeof action === 'string' && action.trim().length === 0)) {
      if (card && card.type === 'location' && card.locations && card.locations.length > 0) {
        // Persist the first location as a non-temporary pin
        const location = card.locations[0];
        // console.log('📍 Persisting location pin:', location.name, 'at', location.location);
        try {
          if (typeof onPersistLocationPin === 'function' && location.location) {
            const lat = Number(location.location.lat);
            const lng = Number(location.location.lng);
            const persistedPin = {
              id: `ai-persist-${location.id || 'pin'}-${lat.toFixed(6)}-${lng.toFixed(6)}`,
              name: location.name || 'Location',
              description: location.description || '',
              coordinates: { lat, lng },
              confidence: 1.0
            };
            // console.log('📍 Persisted pin object:', persistedPin);
            onPersistLocationPin(persistedPin);
          }
        } catch (e) {
          console.error('Failed to persist location pin:', e);
        }

        // Auto-send a brief follow-up asking for details about this place
        const tellMoreText = location?.name
          ? `Tell me more about ${location.name}`
          : 'Tell me more about this place';
        const autoMeta = {
          selected_location_card: true,
          location_card_id: location.id,
          location_card_title: location.name,
          location_card_coordinates: location.location || null
        };
        handleSendMessage(tellMoreText, null, autoMeta);
        return;
      }
      // For non-location cards with no action, default to title or input_template
      action = (card?.input_template && card.input_template.trim()) || (card?.title && `Let's ${card.title}`) || 'Let’s do this step';
    }
    
    // 🎯 CONTINUITY FIX: Add metadata to indicate this was a card selection
    const cardMetadata = {};
    
    if (card && card.type === 'location') {
      // Explore card selected - user wants to see location
      cardMetadata.selected_location_card = true;
      cardMetadata.location_card_id = card.id;
      cardMetadata.location_card_title = card.title;
      if (card.location) {
        cardMetadata.location_card_coordinates = card.location;
      }
      // console.log('📍 User selected explore card:', card.title);
    } else if (card && (card.type === 'observe' || card.type === 'learn')) {
      // Action card selected - user wants to do this
      cardMetadata.selected_action_card = true;
      cardMetadata.action_card_id = card.id;
      cardMetadata.action_card_title = card.title;
      cardMetadata.action_card_type = card.type;
      cardMetadata.action_card_intent = card.intent;
      // Include full payload for backend to use
      cardMetadata.action_card_payload = card;
      // Include structured data if present (from form submission)
      if (card.structured_data) {
        cardMetadata.structured_data = card.structured_data;
      }
      // console.log('🎯 User selected action card:', card.title, 'intent:', card.intent);
    }
    
    // For explore cards, send a specific request for multiple locations
    const outgoing = (card && card.type === 'location')
      ? `Show me multiple nearby locations for ${card?.title || 'exploration'}. Generate 3-5 different places I can visit.`
      : action;
    // Intent-driven behavior - clean and explicit
    // Structured data form submission - send message with structured data (check FIRST to prevent photo flow)
    const hasStructuredData = card?.structured_data && card.intent === 'capture_structured_data';
    // Don't trigger photo flow for structured data cards (they show forms instead)
    const wantsPhoto = (card?.intent === 'capture_photo' || card?.type === 'observe') && card?.intent !== 'capture_structured_data';
    const wantsLearnMore = card?.intent === 'provide_info' || card?.type === 'learn';
    // text flow: prefill composer when evidence requests text/description/note
    const wantsText = (card?.evidence && (card.evidence.type === 'text' || card.evidence.type === 'description' || card.evidence.type === 'note')) || (card?.intent === 'record_note');
    
    // Handle structured data form submission
    if (hasStructuredData) {
      const formMessage = card.structured_data?.form_card_id 
        ? `Completed ${card.title || 'form'}`
        : card.title 
          ? `Completed ${card.title}`
          : 'Form submitted';
      
      // Extract photo URLs from structured data form submission for vision analysis
      // Photos in structured data forms should be analyzed the same way as regular observer card photos
      let formImageUrl = null;
      if (card.structured_data?.fields && card.structured_data?.values) {
        // Find photo fields from the fields array
        const photoFieldIds = card.structured_data.fields
          .filter(field => field.type === 'photo' && field.value)
          .map(field => field.id);
        
        // Extract photo URLs from values using photo field IDs
        for (const fieldId of photoFieldIds) {
          const photoUrl = card.structured_data.values[fieldId];
          if (photoUrl && typeof photoUrl === 'string' && photoUrl.trim()) {
            formImageUrl = photoUrl; // Use first photo for vision analysis
            break; // Analyze the first photo (backend can be extended to handle multiple photos later)
          }
        }
      }
      
      handleSendMessage(formMessage, formImageUrl, cardMetadata);
      return;
    }
    if (wantsPhoto) {
      const overlayHint = card?.instruction_image_url || card?.overlay_hint || null;
      try {
        setExternalCommand({ type: 'open_camera', prefillText: '', autoSend: true, overlayHint, metadata: cardMetadata });
      } catch (_) {}
      return;
    }
    if (wantsLearnMore) {
      const learnMorePrompt = `Tell me comprehensive details about ${card?.title || 'this topic'}`;
      // Auto-submit the learn more request
      handleSendMessage(learnMorePrompt, null, { ...cardMetadata, learn_more: true });
      return;
    }
    if (wantsText) {
      const prefillText = card?.input_template || `${card?.title || 'Observation'}: `;
      try {
        setExternalCommand({ type: 'prefill_text', prefillText, autoSend: false, metadata: cardMetadata });
      } catch (_) {}
      return;
    }
    handleSendMessage(outgoing, null, cardMetadata);
    
    // Force scroll to bottom after sending the message (bypasses user scroll state)
    setTimeout(() => {
      if (forceScrollToBottom) {
        forceScrollToBottom();
      }
    }, 150);
    } catch (error) {
      console.error('Error handling card click:', error);
      // Show user-friendly error message
      if (typeof handleSendMessage === 'function') {
        handleSendMessage("Sorry, there was an error processing that action. Please try again.", null, { error: true });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReadOnly, handleSendMessage, forceScrollToBottom, effectiveProjectId, onRequestFinishProject, onObjectiveConfirmed, onPersistLocationPin, effectiveProject, queryClient, allObjectiveMatchIds, setObjectiveMatchStatuses]);

  // Handle spotlight "Learn More" clicks
  const handleLearnMore = useCallback((learnMorePrompt, spotlight) => {
    // Disable all interactions in read-only mode
    if (isReadOnly) {
      return;
    }
    
    // console.log('🔍 Learn more clicked for:', spotlight.entity, '→', learnMorePrompt);
    
    // 🎯 CONTINUITY FIX: Add metadata to indicate this is a learn-more request
    const learnMoreMetadata = {
      learn_more: true,
      spotlight: spotlight,
      spotlight_entity: spotlight?.entity || spotlight?.title
    };
    
    handleSendMessage(learnMorePrompt, null, learnMoreMetadata);
    
    // Force scroll to bottom after sending the message (bypasses user scroll state)
    setTimeout(() => {
      if (forceScrollToBottom) {
        forceScrollToBottom();
      }
    }, 150);
  }, [isReadOnly, handleSendMessage, forceScrollToBottom]);

  const handleStopGeneration = useCallback(() => {
    // Disable all interactions in read-only mode
    if (isReadOnly) {
      return;
    }
    
    if (effectiveChatId && isConnected) {
      sendWebSocketMessage({
        type: 'stop_generation',
        chatId: effectiveChatId,
        data: { chatId: effectiveChatId }
      });
    }
    setIsGenerating(false);
  }, [isReadOnly, effectiveChatId, isConnected, sendWebSocketMessage]);

  // Auto-select first chat if none is active (server creates chat)
  useEffect(() => {
    if (!isReadOnly && !effectiveChatId && Array.isArray(chatsData) && chatsData.length > 0) {
      const firstChat = chatsData[0];
      if (firstChat?.id && effectiveSetActiveChatId) {
        effectiveSetActiveChatId(firstChat.id);
      }
    }
  }, [isReadOnly, effectiveChatId, chatsData, effectiveSetActiveChatId]);

  // Clear interactive cards and cropped images when switching chats
  // Only clear when chatId actually changes from one valid ID to another
  const prevChatIdRef = useRef(effectiveChatId);
  useEffect(() => {
    const prevId = prevChatIdRef.current;
    const currentId = effectiveChatId;
    
    // Only clear cards when switching between two valid chat IDs
    // Don't clear on initial load (null -> valid ID) or when going to null
    if (prevId && currentId && prevId !== currentId) {
      console.debug('[cards] chat id changed, clearing cards', { from: prevId, to: currentId });
      setChatState(prev => ({
        ...prev,
        messageCards: new Map(),
        croppedImages: new Map()
      }));
    }
    
    prevChatIdRef.current = currentId;
  }, [effectiveChatId]);

  // Client auto-welcome disabled; server sends welcome on project start

  const handleChatSelect = useCallback((chatId) => {
    navigate(`/projects/${effectiveProjectId}/chats/${chatId}`);
    effectiveSetActiveChatId(chatId);
    setActiveTab('chat');
    resetScrollState();
  }, [effectiveProjectId, navigate, effectiveSetActiveChatId, resetScrollState]);

  const handleDeleteChat = useCallback((chatId) => {
    // Use window.confirm to avoid ESLint error, or implement a custom modal later
    if (window.confirm('Delete this nature conversation?')) {
      deleteChatMutation.mutate(chatId);
    }
  }, [deleteChatMutation]);

  // Do not block rendering while chats load; allow the main UI to render immediately

  // (Moved read-only empty state into render to keep hooks top-level)

  // Debug logging
  // console.log('ChatSection render debug:', {
  //   effectiveChatId,
  //   chatId,
  //   activeChatId,
  //   activeTab,
  //   isConnected,
  //   allMessagesLength: allMessages?.length || 0,
  //   hasAllMessages: !!allMessages
  // });

  // Show loader when text is streaming but cards for that streaming message haven't arrived yet
  const isAwaitingCards = useMemo(() => {
    // Helper to check if message has cards in metadata (for saved messages)
    // This prevents spinner from persisting if cards are already in the message history but not in messageCards Map
    const hasCardsInMetadata = (messageId) => {
        if (!allMessages || !Array.isArray(allMessages)) return false;
        const msg = allMessages.find(m => m.id === messageId);
        const cards = msg?.message_metadata?.interactive_cards;
        return Boolean(Array.isArray(cards) && cards.length > 0);
    };

    // Spinner during streaming while cards not yet present
    const duringStreaming = Boolean(
      isGenerating &&
      streamingMessage?.id &&
      streamingMessage?.content &&
      !messageCards.has(streamingMessage.id)
    );
    // Spinner after streaming completes while waiting for cards for the saved AI message id
    const afterStreaming = Boolean(
      awaitingCardsMessageId && 
      !messageCards.has(awaitingCardsMessageId) &&
      !hasCardsInMetadata(awaitingCardsMessageId)
    );
    return duringStreaming || afterStreaming;
  }, [isGenerating, streamingMessage?.id, streamingMessage?.content, messageCards, awaitingCardsMessageId, allMessages]);

  // Clear awaiting-cards state once cards for that message are present
  useEffect(() => {
    if (awaitingCardsMessageId && messageCards.has(awaitingCardsMessageId)) {
      setAwaitingCardsMessageId(null);
    }
  }, [awaitingCardsMessageId, messageCards]);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      {/* <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-green-500"></div>
            <span className="text-sm font-medium text-gray-700">{projectResponse?.data?.name || 'OKIO'}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setActiveTab('chat')}
              className={`px-3 py-1 text-xs rounded-md transition-colors ${
                activeTab === 'chat' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
              }`}
            >
              <MessageCircle size={12} className="inline mr-1" />
              Chat
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`px-3 py-1 text-xs rounded-md transition-colors ${
                activeTab === 'history' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
              }`}
            >
              <History size={12} className="inline mr-1" />
              History
            </button>
          </div>
          <button
            onClick={handleNewChat}
            disabled={createChatMutation.isPending}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            title="New nature conversation"
          >
            <Plus size={16} />
          </button>
        </div>
      </div> */}

      {/* Content */}
      {activeTab === 'chat' ? (
        <div className="flex flex-col h-full">
          {/* Read-only banner - Sticky at top, show when viewing archived/read-only chats */}
          {effectiveChatId && isReadOnly && (
            <div className="sticky top-0 z-10 p-4 w-full border-y-2 border-yellow-300 dark:border-yellow-600 bg-yellow-50 dark:bg-yellow-900/20 shadow-sm">
              <div className="flex items-center gap-2 text-yellow-800 dark:text-yellow-200 max-w-3xl mx-auto">
                <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span className="text-xs font-medium">Read-only mode: This is another user's session or an archived chat. You can view but not edit.</span>
              </div>
            </div>
          )}
          
          {/* Messages Container */}
          <div 
            ref={containerRef}
            className="flex-1 overflow-y-auto mobile-scroll overscroll-contain"
            onScroll={handleScroll}
          >
            {/* Load More Button - Hidden in read-only mode */}
            {hasMore && !isReadOnly && (
              <div className="flex justify-center p-4">
                <button
                  onClick={loadMore}
                  disabled={isLoadingMore}
                  className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {isLoadingMore ? (
                    <>
                      <Loader2 className="animate-spin" size={24} />
                    </>
                  ) : (
                    <>
                      <ChevronUp size={14} />
                      Load earlier messages
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Messages */}
            <div className="px-4 pt-0 pb-4 md:pt-8 md:pb-8 2xl:px-0 max-w-3xl mx-auto">
              {/* Project Header - Clean, focused design */}
              {effectiveProject?.name && (
                <div className="mb-5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl overflow-hidden">
                  {/* Main header row */}
                  <div className="p-4 sm:p-5">
                    {/* Title row with game mode badge */}
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white leading-tight" aria-label={`Project: ${effectiveProject.name}`}>
                        {effectiveProject.name}
                      </h1>
                      
                      {/* Game Mode Badge - only for multiplayer modes */}
                      {effectiveProject?.gameMode && effectiveProject?.isMultiplayer && (
                        <div className="flex-shrink-0">
                          {effectiveProject.gameMode === 'collaborative' && (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300" role="status">
                              🤝 Team
                            </span>
                          )}
                          {effectiveProject.gameMode === 'competitive' && (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300" role="status">
                              🏆 Race
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Description - always show if exists */}
                    {effectiveProject?.description && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed line-clamp-2">
                        {effectiveProject.description}
                      </p>
                    )}

                    {/* Compact metadata row */}
                    {/* <div className="flex items-center justify-between gap-3 text-[12px] text-gray-500 dark:text-gray-400">
                      <div className="flex items-center gap-3 flex-wrap">
                        {effectiveProject?.isMultiplayer ? (
                          <span className="flex items-center gap-1">
                            <span>👥</span>
                            <span>{(effectiveProject.participantCount || 0) + 1} participant{(effectiveProject.participantCount || 0) + 1 === 1 ? '' : 's'}</span>
                          </span>
                        ) : effectiveProject?.owner && (
                          <span className="flex items-center gap-1">
                            <span>👤</span>
                            <span>Created by {effectiveProject.owner.firstName || effectiveProject.owner.username || 'System'}</span>
                          </span>
                        )}
                      </div>
                      {effectiveProject?.status === 'ongoing' && (
                        <span className="flex items-center gap-1.5 text-green-600 dark:text-green-400 font-medium">
                          <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                          Live
                        </span>
                      )}
                    </div> */}
                  </div>
                </div>
              )}
              
              {/* Invite Team Card - Show for ongoing projects (single-player or multiplayer) */}
              {effectiveProject?.status === 'ongoing' && (
                <InviteTeamCard
                  project={effectiveProject}
                  projectId={effectiveProjectId}
                  isOwner={effectiveProject?.ownerId === user?.id}
                  onTeamUpdate={() => {
                    // Refetch project to get updated isMultiplayer status
                    queryClient.invalidateQueries(createQueryKey.project(effectiveProjectId));
                  }}
                />
              )}

              {/* Project Objectives Card - Shows setup or reminder based on objectives */}
              <ProjectObjectivesCard 
                project={effectiveProject}
                onOpenGoalsModal={onOpenGoalsModal}
              />
              
              {/* ✅ SIMPLIFIED: Show messages area as soon as we have any content OR are generating */}
              {isReadOnly && !effectiveChatId ? (
                <div className="text-center py-12">
                  <div className="text-lg font-medium text-gray-900 dark:text-white mb-2">No chat selected</div>
                  <div className="text-sm text-gray-600">This adventure doesn't have a chat history.</div>
                </div>
              ) : (
                <>
                  {allMessages?.length > 0 || isGenerating ? (
                  <div>
                    {allMessages?.map((message) => (
                      <div key={message.client_message_id || message.id} className={message.role === 'user' ? 'card-base' : ''}>
                        <StreamingMessage
                          message={message}
                          onImageClick={openViewerForMessage}
                          onRetry={handleRetryMessage}
                        />
                        {/* Show all cards for assistant messages (fallback to persisted metadata) */}
                        {message.role === 'assistant' && (() => {
                          const persisted = (message?.message_metadata?.interactive_cards || []);
                          const entryCards = extractCardsFromEntry(messageCards.get(message.id));
                          const cardsToRender = entryCards.length > 0
                            ? entryCards
                            : (Array.isArray(persisted) ? persisted : []);
                          if (!Array.isArray(cardsToRender) || cardsToRender.length === 0) {
                            return false;
                          }
                          return (
                            <CardErrorBoundary>
                              <InteractiveCards
                                cards={[...cardsToRender]}
                                onCardClick={handleCardClick}
                                messageId={message.id}
                                chatIdForStreaming={effectiveChatId}
                                registerHandler={registerHandler}
                                handledCardIds={Array.isArray(message?.message_metadata?.handled_cards) ? message.message_metadata.handled_cards : []}
                                cardStates={message?.message_metadata?.card_states || {}}
                                croppedImages={croppedImages.get(message.id) || {}}
                                sourceImageUrl={getSourceImageForAssistantMessage(message.id)}
                                assistantMessageId={message.id}
                                onInteractiveCardInteraction={onInteractiveCardInteraction}
                                onLocationCardExpanded={handleLocationCardExpanded}
                                onLocationCardCollapsed={handleLocationCardCollapsed}
                                userLocation={userLocation}
                                allMessages={allMessages}
                                objectiveMatchStatuses={objectiveMatchStatuses}
                                onOpenSpotlightViewer={({ spotlight, assistantMessageId, sourceImageUrl }) => {
                                  // Open viewer for this assistant message with highlighted spotlight
                                  const cards = extractCardsFromEntry(messageCards.get(assistantMessageId));
                                  const spotlights = cards.filter(c => c.type === 'spotlight');
                                  const imageUrl = sourceImageUrl || (allMessages?.find(m => {
                                    const meta = m?.message_metadata || {};
                                    return m.role === 'assistant' && m.id === assistantMessageId && meta.source_image_url;
                                  })?.message_metadata?.source_image_url) || (allMessages?.find(m => m.role === 'user' && m?.message_metadata?.imageUrl)?.message_metadata?.imageUrl) || null;
                                  if (!imageUrl) return;
                                  setViewerImageUrl(imageUrl);
                                  setViewerSpotlights(spotlights);
                                  setViewerOpen(true);
                                  setHighlightedSpotlightId(spotlight?.id || null);
                                }}
                                onLearnMore={handleLearnMore}
                                isReadOnly={isReadOnly}
                                projectId={projectId}
                              />
                            </CardErrorBoundary>
                          );
                        })()}
                      </div>
                    ))}

                    {/* Show loading indicator while waiting for assistant, even if optimistic message was cleared */}
                    {isGenerating && !streamingMessage?.content && (
                      <div className="flex items-center justify-start mb-4">
                        <LoadingIndicator />
                      </div>
                    )}

                    {/* While waiting for cards to be generated for the current streaming message, show loader again */}
                    {isAwaitingCards && (
                      <div className="flex items-center justify-start mb-4">
                        <LoadingIndicator />
                      </div>
                    )}
                  </div>
              ) : isMessagesLoading || !effectiveProject ? (
                <div className="flex items-center justify-center p-6">
                  <Loader2 className="animate-spin text-gray-400" size={20} />
                </div>
              ) : isWaitingForWelcome && !isReadOnly ? (
                <div className="flex flex-col items-center justify-center p-6 text-center">
                  <div className="mb-4">
                    <Loader2 className="animate-spin text-green-500" size={24} />
                  </div>
                  <div className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    Preparing your project...
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Setting up your project
                  </div>
                </div>
              ) : welcomeTimeout && !isReadOnly ? (
                <div className="flex flex-col items-center justify-center p-6 text-center">
                  <div className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    Welcome to your project!
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Ready to explore and discover?
                  </div>
                  <button
                    onClick={retryWelcomeMessage}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                  >
                    Start Exploring
                  </button>
                </div>
              ) : effectiveProject?.status === 'ongoing' && !isReadOnly ? (
                <div className="flex items-center justify-start">
                  <LoadingIndicator 
                    className="px-4 bg-gray-50 dark:bg-gray-800" 
                    showText={true} 
                    text="Preparing your project..." 
                  />
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-sm text-gray-500 dark:text-gray-400">No messages yet.</div>
                </div>
              )}
                </>
              )}
            </div>
            
            <div ref={endRef} />
          </div>

          {/* Chat Input - Hidden in read-only mode - Fixed at bottom */}
          {effectiveChatId && !isReadOnly && (
            <IsolatedChatInput
              onSendMessage={handleSendMessage}
              isConnected={isConnected}
              isGenerating={isGenerating}
              onStopGeneration={handleStopGeneration}
              projectId={effectiveProjectId}
              onSubmittedWithImage={onSubmittedWithImage}
              externalCommand={externalCommand}
              onExternalCommandHandled={() => setExternalCommand(null)}
              isReadOnly={isReadOnly}
            />
          )}
          
        </div>
      ) : (
        /* History Tab */
        <div className="flex-1 overflow-y-auto mobile-scroll">
          <ChatHistoryList
            chats={chatsData}
            activeChatId={effectiveChatId}
            onChatSelect={handleChatSelect}
            onDeleteChat={handleDeleteChat}
            isLoading={isChatsLoading}
          />
        </div>
      )}
  {/* Fullscreen viewer */}
  <FullscreenImageSpotlightViewer
    isOpen={viewerOpen}
    imageUrl={viewerImageUrl}
    spotlights={viewerSpotlights}
    onClose={closeViewer}
    highlightSpotlightId={highlightedSpotlightId}
  />

      {/* Objective Fulfillment Notifications */}
      {effectiveProject?.isMultiplayer && (
        <ParticipantNotificationManager 
          notifications={objectiveNotifications}
          onConsumed={(id) => {
            setChatState(prev => ({
              ...prev,
              objectiveNotifications: prev.objectiveNotifications.filter(n => n.id !== id)
            }));
          }}
        />
      )}
    </div>
  );
};

export default ChatSection;